import { SubmitHandler } from 'react-hook-form';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { Avatar } from '@givebutter/primitives/avatar';
import { Badge } from '@givebutter/primitives/badge';
import { Button } from '@givebutter/primitives/button';
import { Label } from '@givebutter/primitives/label';
import * as Popover from '@givebutter/primitives/popover';
import * as RadioGroup from '@givebutter/primitives/radio-group';
import { Separator } from '@givebutter/primitives/separator';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { useToast } from '@givebutter/primitives/toast';
import { maskDecimal, maskWholeNumber } from '@givebutter/util';
import { faCheck, faQuestionCircle } from '@fortawesome/pro-regular-svg-icons';
import { faBolt, faHeart } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchCampaignPayoutOptions, initiateTransfer } from 'api/payouts';
import { NotEligible } from 'assets/js/pages/payouts/NotEligible';
import { useFetchPayoutAccount } from 'assets/js/pages/payouts/useFetchPayoutAccount';
import { useWithdrawFunds } from 'assets/js/pages/payouts/useWithdrawFunds';
import * as Form from 'components/form';
import { PAYOUTS_HELP_LINK } from 'constants/links';
import { useCurrencySymbol } from 'customHooks/useCurrencySymbol';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { accountQueryKeys } from 'queries/account-query-keys';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { twMerge } from 'tailwind-merge';
import { PayoutType } from 'types/domain';
import { GivebutterAxiosError } from 'types/error';

interface WithdrawFundsDialogProps extends AlertDialog.AlertDialogRootProps {}

interface WithdrawFundsFormValues {
    tip: '0' | '0.01' | '0.03' | '0.05';
    type: PayoutType;
    saveDefaultTip: boolean;
}

const INSTANT_PAYOUT_MAX = 9999;
const SYSTEM_DEFAULT_TIP = '0.03';
const TIP_PERCENTAGES = [0, 0.01, 0.03, 0.05];

const WithdrawFundsDialog = (props: WithdrawFundsDialogProps) => {
    const { children, ...rest } = props;
    const { campaign, transferSent } = useWithdrawFunds();
    const isEligible = campaign.payouts.can_payout_bank.validated;
    const queryClient = useQueryClient();
    const { accountId } = useCurrentAccount();

    return (
        <AlertDialog.Root
            type={isEligible ? 'info' : 'error'}
            onOpenChange={open => {
                if (!open && transferSent) {
                    queryClient.invalidateQueries({
                        queryKey: accountQueryKeys.current(accountId),
                    });
                }
            }}
            {...rest}
        >
            {children}
            <AlertDialog.Portal>
                <AlertDialog.Overlay />
                <AlertDialog.Content
                    className={twMerge(
                        'max-h-[90vh] max-w-[30rem] overflow-y-auto',
                        transferSent && 'max-w-[25rem]',
                    )}
                >
                    <AlertDialog.Close />
                    <AlertDialog.Body>
                        {!isEligible ? <NotEligible /> : <WithdrawFundsDialogContent />}
                    </AlertDialog.Body>
                </AlertDialog.Content>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
};

const WithdrawFundsDialogContent = () => {
    const { campaign, transferSent, setTransferSent } = useWithdrawFunds();
    const { instantPayouts = false } = useFlags();
    const currencySymbol = useCurrencySymbol();
    const {
        accountId,
        defaultPayoutTip,
        instantEligible: isAccountInstantEligible,
    } = useCurrentAccount();
    const { data: payoutAccount } = useFetchPayoutAccount();
    const { error } = useToast();

    const isWithinInstantPayoutLimit =
        campaign.balance?.available && campaign.balance.available <= INSTANT_PAYOUT_MAX;
    const isInstantEligible =
        instantPayouts &&
        isAccountInstantEligible &&
        isWithinInstantPayoutLimit &&
        campaign.payouts.can_payout_bank.instant_eligible;
    const availableBalance = campaign.balance?.available ?? 0;
    const defaultTip = (
        defaultPayoutTip !== null && TIP_PERCENTAGES.includes(defaultPayoutTip)
            ? defaultPayoutTip.toString()
            : SYSTEM_DEFAULT_TIP
    ) as WithdrawFundsFormValues['tip'];
    const methods = Form.useForm<WithdrawFundsFormValues>({
        defaultValues: {
            tip: defaultTip,
            type: 'standard',
            saveDefaultTip: false,
        },
    });

    const { watch, control, path, handleSubmit } = methods;
    const [tip, type, saveDefaultTip] = watch(['tip', 'type', 'saveDefaultTip']);
    const showSaveSettings = tip !== defaultTip;
    const { data } = useSuspenseQuery({
        queryKey: campaignQueryKeys.payoutOptions(campaign.id, TIP_PERCENTAGES, availableBalance),
        queryFn: () =>
            fetchCampaignPayoutOptions(accountId, campaign.id, {
                available_tip_percentages: TIP_PERCENTAGES,
            }),
    });

    const selectedAmount = data.amounts[tip];
    const {
        standard_amount,
        instant_amount,
        instant_fee,
        standard_fee,
        tip: computedTip,
    } = selectedAmount;

    const isStandardPayout = type === 'standard';
    const payoutAmount = isStandardPayout ? standard_amount : instant_amount;
    const fee = isStandardPayout ? standard_fee : instant_fee;

    const instantFeeRate = campaign.payouts.can_payout_bank.instant_fee_rate;
    const tipPercentage = parseFloat(tip) * 100;

    const last4 = `${payoutAccount.data?.external_account?.bank_name ?? payoutAccount.data?.external_account?.brand} ••••${payoutAccount.data?.external_account?.last4}`;
    const payoutTimingMsg = isStandardPayout
        ? 'Funds typically appear in your account within 1-2 business days. Payouts made on the weekend or during holidays will be processed the next business day. All payouts are subject to a review by our Trust & Safety team.'
        : 'Funds typically appear in your account within 30 minutes. In rare cases, a review by our Trust & Safety team may cause a delay.';
    const transferFee = isStandardPayout ? 'Transfer fee' : `Transfer fee (${instantFeeRate}%)`;

    const { isPending: isLoading, mutate: initiateWithdrawal } = useMutation({
        mutationFn: (values: WithdrawFundsFormValues) =>
            initiateTransfer(accountId, {
                campaignId: campaign.id,
                tip: computedTip,
                amount: availableBalance,
                payout: payoutAmount,
                type: values.type,
                fee: fee,
                default_payout_tip: saveDefaultTip ? parseFloat(tip) : undefined,
                instant_eligible: isInstantEligible,
            }),
        onSuccess: () => {
            setTransferSent(true);
        },
        onError: (e: GivebutterAxiosError) => {
            error(e.response?.data?.message ?? 'Failed to initiate bank transfer.');
        },
    });

    return (
        <Form.Provider {...methods}>
            <div className={'flex flex-col gap-4 text-base'}>
                <form
                    className={'contents'}
                    onSubmit={handleSubmit(
                        initiateWithdrawal as SubmitHandler<WithdrawFundsFormValues>,
                    )}
                >
                    {transferSent && (
                        <div className="flex flex-col gap-6">
                            <Avatar className="bg-green-100">
                                <FontAwesomeIcon
                                    icon={faCheck}
                                    className="text-2xl text-green-600"
                                />
                            </Avatar>
                            <div>
                                <p className="text-lg font-medium">
                                    Your {!isStandardPayout && 'instant '}
                                    transfer has been initiated!
                                </p>
                            </div>
                            <div className="flex flex-col gap-2.5 rounded-lg bg-gray-50 p-4">
                                <p
                                    className={
                                        'text-xs uppercase tracking-[0.075rem] text-gray-500'
                                    }
                                >
                                    Transfer summary
                                </p>
                                <p className="text-sm text-gray-600">Sending to {last4}</p>
                                <div className={'flex flex-col gap-1.5'}>
                                    <p className={'flex items-center justify-between gap-2'}>
                                        <span className="text-sm text-gray-600">
                                            Tip ({tipPercentage}%)
                                        </span>
                                        <span className="text-sm text-gray-900">
                                            -{currencySymbol}
                                            {maskDecimal(computedTip)}
                                        </span>
                                    </p>
                                    <p className={'flex items-center justify-between gap-2'}>
                                        <span className="text-sm text-gray-600">{transferFee}</span>
                                        <span className="text-sm text-gray-900">
                                            {fee === 0
                                                ? 'Free'
                                                : `-${currencySymbol}${maskDecimal(fee)}`}
                                        </span>
                                    </p>
                                    <Separator />
                                    <p className="flex items-center justify-between gap-4">
                                        <span className="text-sm font-medium text-gray-600">
                                            Total transferred
                                        </span>
                                        <span className="text-2xl font-medium">
                                            {currencySymbol}
                                            {maskDecimal(payoutAmount)}
                                        </span>
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {!transferSent && (
                        <>
                            <AlertDialog.Title>Withdraw funds</AlertDialog.Title>
                            <div className={'flex flex-wrap justify-between gap-4'}>
                                <div className={'flex items-center gap-0.5'}>
                                    <p className={'text-gray-700'}>Campaign balance</p>
                                    <Popover.Root>
                                        <Popover.Trigger asChild>
                                            <Button
                                                variant="transparent"
                                                size="sm"
                                                shape="square"
                                                className="radix-state-open:bg-gray-100 hover:bg-gray-100"
                                            >
                                                <FontAwesomeIcon
                                                    className="text-gray-900"
                                                    icon={faQuestionCircle}
                                                />
                                            </Button>
                                        </Popover.Trigger>
                                        <Popover.Portal>
                                            <Popover.Content className="min-w-48 max-w-[22.5rem] bg-black px-3 py-2 !text-sm text-white">
                                                Funds available to be withdrawn to your connected
                                                account.
                                                <Popover.Arrow className="fill-black" />
                                            </Popover.Content>
                                        </Popover.Portal>
                                    </Popover.Root>
                                </div>
                                <p className={'font-medium'}>
                                    {currencySymbol}
                                    {maskDecimal(availableBalance)}
                                </p>
                            </div>
                            <Separator />
                            {instantPayouts && isAccountInstantEligible && (
                                <Form.Container className="@container gap-2">
                                    <Form.Label
                                        name={path('type')}
                                        className={'text-base font-medium'}
                                    >
                                        Transfer options
                                    </Form.Label>
                                    <Form.UseFormRadioGroup
                                        name={path('type')}
                                        className={'@sm:grid-cols-2 grid grid-cols-1 gap-2'}
                                        variant={'card'}
                                    >
                                        <RadioGroup.Item
                                            id={'instant-payout'}
                                            value={'instant'}
                                            className={
                                                'flex gap-3 disabled:bg-gray-50 disabled:text-gray-500 disabled:opacity-100'
                                            }
                                            disabled={!isInstantEligible}
                                        >
                                            <RadioGroup.IndicatorContainer>
                                                <RadioGroup.Indicator />
                                            </RadioGroup.IndicatorContainer>
                                            <div className={'flex flex-1 flex-col gap-1'}>
                                                <Label
                                                    className={
                                                        'flex items-center justify-between gap-2 text-base'
                                                    }
                                                    htmlFor={'instant-payout'}
                                                >
                                                    <span className="flex items-center gap-1">
                                                        Instant
                                                        <FontAwesomeIcon
                                                            icon={faBolt}
                                                            className={twMerge(
                                                                'text-brand-butter-primary',
                                                                !isInstantEligible &&
                                                                    'text-gray-500',
                                                            )}
                                                        />
                                                    </span>
                                                    <Badge
                                                        className={twMerge(
                                                            'bg-gray-50 text-xs text-gray-700',
                                                            !isInstantEligible && 'text-gray-500',
                                                        )}
                                                    >
                                                        {instantFeeRate}%
                                                    </Badge>
                                                </Label>
                                                <p className={'relative text-sm text-gray-600'}>
                                                    {isInstantEligible && (
                                                        <>Transfer arrives within 30 mins</>
                                                    )}

                                                    {!isInstantEligible && (
                                                        <span className="text-gray-500">
                                                            {isWithinInstantPayoutLimit
                                                                ? 'Not available for this withdrawal'
                                                                : `Not available for withdrawals above ${currencySymbol}${maskWholeNumber(INSTANT_PAYOUT_MAX)}`}
                                                            .{' '}
                                                            <a
                                                                href={PAYOUTS_HELP_LINK}
                                                                target="_blank"
                                                                rel="noreferrer"
                                                            >
                                                                Learn more
                                                            </a>
                                                        </span>
                                                    )}
                                                </p>
                                            </div>
                                        </RadioGroup.Item>
                                        <RadioGroup.Item
                                            id={'standard-payout'}
                                            value={'standard'}
                                            className={'flex gap-3'}
                                        >
                                            <RadioGroup.IndicatorContainer>
                                                <RadioGroup.Indicator />
                                            </RadioGroup.IndicatorContainer>
                                            <div className={'flex flex-1 flex-col gap-1'}>
                                                <Label
                                                    className={
                                                        'flex items-center justify-between gap-2 text-base'
                                                    }
                                                    htmlFor={'standard-payout'}
                                                >
                                                    Standard
                                                    <Badge className="bg-gray-50 text-xs text-gray-700">
                                                        Free
                                                    </Badge>
                                                </Label>
                                                <p className={'text-sm text-gray-600'}>
                                                    Transfer arrives in 1-2 business days
                                                </p>
                                            </div>
                                        </RadioGroup.Item>
                                    </Form.UseFormRadioGroup>
                                </Form.Container>
                            )}
                            <div className={'flex flex-col justify-between gap-4'}>
                                <div className={'flex flex-col'}>
                                    <div className={'flex items-center gap-2'}>
                                        <Form.Label
                                            name={path('tip')}
                                            className={'text-base font-medium'}
                                        >
                                            Leave a tip for Givebutter
                                        </Form.Label>
                                        <FontAwesomeIcon
                                            className={'text-brand-butter-primary'}
                                            icon={faHeart}
                                        />
                                    </div>
                                    <p className={'text-sm text-gray-600'}>
                                        Your generosity helps us continue to provide 150+ free
                                        fundraising features and 24/7 real human support for
                                        changemakers like you.
                                    </p>
                                </div>

                                <Form.SingleSelect control={control} name="tip">
                                    <SingleSelect.Trigger aria-label="Tip" />
                                    <SingleSelect.Content>
                                        <SingleSelect.Item value="0.05">
                                            <SingleSelect.ItemText>5%</SingleSelect.ItemText>
                                        </SingleSelect.Item>
                                        <SingleSelect.Item value="0.03">
                                            <SingleSelect.ItemText>
                                                3% (most common)
                                            </SingleSelect.ItemText>
                                        </SingleSelect.Item>
                                        <SingleSelect.Item value="0.01">
                                            <SingleSelect.ItemText>1%</SingleSelect.ItemText>
                                        </SingleSelect.Item>
                                        <SingleSelect.Item value="0">
                                            <SingleSelect.ItemText>0%</SingleSelect.ItemText>
                                        </SingleSelect.Item>
                                    </SingleSelect.Content>
                                </Form.SingleSelect>
                                {showSaveSettings && (
                                    <Form.Container className="flex flex-row items-center gap-2">
                                        <Form.UseFormCheckboxInput
                                            control={control}
                                            name={path('saveDefaultTip')}
                                        />
                                        <Form.Label name={path('saveDefaultTip')}>
                                            Remember this in the future
                                        </Form.Label>
                                    </Form.Container>
                                )}
                            </div>
                            <Separator />
                            <div className={'flex flex-col gap-2.5'}>
                                <p
                                    className={
                                        'text-xs uppercase tracking-[0.075rem] text-gray-500'
                                    }
                                >
                                    Transfer summary
                                </p>
                                <p className="text-sm text-gray-600">Sending to {last4}</p>
                                <Separator />
                                <div className={'flex flex-col gap-1.5'}>
                                    <p className={'flex items-center justify-between gap-2'}>
                                        <span className="text-sm text-gray-600">
                                            Tip ({tipPercentage}%)
                                        </span>
                                        <span className="text-sm text-gray-900">
                                            -{currencySymbol}
                                            {maskDecimal(computedTip)}
                                        </span>
                                    </p>
                                    <p className={'flex items-center justify-between gap-2'}>
                                        <span className="text-sm text-gray-600">{transferFee}</span>
                                        <span className="text-sm text-gray-900">
                                            {fee === 0
                                                ? 'Free'
                                                : `-${currencySymbol}${maskDecimal(fee)}`}
                                        </span>
                                    </p>
                                </div>
                            </div>
                            <Form.SubmitButton isSubmitting={isLoading}>
                                <span>
                                    Transfer {currencySymbol}
                                    {maskDecimal(payoutAmount)}
                                </span>
                            </Form.SubmitButton>
                        </>
                    )}
                </form>
                <p className={'text-xs text-gray-500'}>{payoutTimingMsg}</p>
            </div>
        </Form.Provider>
    );
};

const WithdrawFundsDialogTrigger = AlertDialog.Trigger;

const Root = WithdrawFundsDialog;
const Trigger = WithdrawFundsDialogTrigger;
const Content = WithdrawFundsDialogContent;

export {
    WithdrawFundsDialog,
    WithdrawFundsDialogTrigger,
    WithdrawFundsDialogContent,
    //
    Root,
    Trigger,
    Content,
};
